import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  computed,
  inject,
} from '@angular/core';
import { ReportsListSectionComponent } from '@app/modules/reports/components/reports/reports-list-section/reports-list-section.component';
import { ProductInventoryItemDocumentsComponent } from '@app/modules/service-data/components/product-inventory-item/product-inventory-item-documents/product-inventory-item-documents.component';
import { ProductInventoryItemUtilsService } from '@app/modules/service-data/services/product-inventory-item-utils.service';
import { SampleUtilsService } from '@app/modules/service-data/services/sample-utils.service';
import { ServiceDataUtilsService } from '@app/modules/service-data/services/service-data-utils.service';
import { GetProductInventoryItemAction } from '@app/modules/service-data/store/product-intentory-item.actions';
import { ProductInventoryItemState } from '@app/modules/service-data/store/product-inventory-item.state';
import { ExtendedProductInventoryItem } from '@app/modules/service-data/types/service-data.types';
import { AccountUtilsService } from '@core/services/account-utils.service';
import { NgbActiveModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { RequirePermissionDirective } from '@shared/directives/require-permission.directive';
import { Observable, Subject, switchMap } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { DocumentPreviewComponent } from '../../document-preview/product-inventory-item-document-preview/document-preview.component';

@Component({
  selector: 'app-product-inventory-item-details',
  imports: [
    AsyncPipe,
    NgbTooltip,
    TranslateModule,
    DatePipe,
    ProductInventoryItemDocumentsComponent,
    DocumentPreviewComponent,
    NgClass,
    RequirePermissionDirective,
    ReportsListSectionComponent,
  ],
  templateUrl: './product-inventory-item-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./product-inventory-item-details.component.scss'],
})
export class ProductInventoryItemDetailsComponent implements OnInit {
  protected dialogRef = inject(NgbActiveModal);
  private store = inject(Store);
  private productInventoryItemUtilsService = inject(
    ProductInventoryItemUtilsService
  );
  private serviceDataUtilsService = inject(ServiceDataUtilsService);
  private sampleUtilsService = inject(SampleUtilsService);
  private authService = inject(AccountUtilsService);

  @Input() piiId: string;

  protected productInventoryItem$: Observable<ExtendedProductInventoryItem>;
  protected onShowPdfSubject$ = new Subject<string>();

  documentPreviewPermission$: Observable<boolean>;
  protected canViewCommentsAndNotes = computed(
    () =>
      this.authService.hasRequiredPermission(
        'serviceData.details.patientState'
      ) || this.authService.hasRequiredPermission('serviceData.details.notes')
  );

  ngOnInit() {
    this.productInventoryItem$ = this.store
      .dispatch(new GetProductInventoryItemAction(this.piiId))
      .pipe(
        switchMap(() =>
          this.store
            .select(ProductInventoryItemState.getProductInventoryItemById)
            .pipe(map((getById) => getById(this.piiId)))
        ),
        switchMap((pii) =>
          this.productInventoryItemUtilsService.getExtendedProductInventoryItem(
            pii,
            {
              partner: true,
              product: true,
              sample: {
                kit: {},
                serviceData: { invoice: true },
              },
            }
          )
        )
      );

    this.documentPreviewPermission$ = this.authService
      .hasRequiredPermission$('pii.details')
      .pipe(first());
  }

  protected openServiceDataDetails(serviceDataId: string) {
    this.serviceDataUtilsService.openDetails(serviceDataId);
  }

  protected openSampleDetails(sampleId: string) {
    this.sampleUtilsService.openDetails(sampleId);
  }

  protected getStatusReason(pii: ExtendedProductInventoryItem) {
    const documentReason = pii.properties.find(
      (p) => p.key === 'DocumentReason'
    )?.value;

    return documentReason ?? '';
  }

  protected readonly getPropertyFromServiceInformation =
    this.serviceDataUtilsService.getPropertyFromServiceInformation;
}
